import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "../../i18n";
import { Reveal, SectionHead } from "./Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-[#232733] rounded-none px-4 py-3.5 text-sm text-white placeholder:text-[#647087] focus:border-[#0055FF] focus:outline-none transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087] mb-2.5";

const Quote = () => {
  const { t } = useLang();
  const f = t.quote.form;
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    product: f.products[0], quantity: f.quantities[0],
    customization: [], details: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleCustom = (opt) => (checked) =>
    setForm((prev) => ({
      ...prev,
      customization: checked ? [...prev.customization, opt] : prev.customization.filter((c) => c !== opt),
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success(t.quote.form.success);
      setForm({ name: "", company: "", email: "", phone: "", product: f.products[0], quantity: f.quantities[0], customization: [], details: "" });
    } catch (err) {
      toast.error(t.quote.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="devis" className="border-t border-[#232733] bg-[#111318] scroll-mt-16" data-testid="quote-section">
      <div className="grid lg:grid-cols-[1fr_1.3fr]">
        <div className="px-6 lg:px-14 py-16 lg:py-28 border-b lg:border-b-0 lg:border-r border-[#232733]">
          <SectionHead index="09" overline={t.quote.overline} title={t.quote.title} desc={t.quote.desc} />
          <Reveal delay={0.15} className="mt-12 space-y-8">
            <div data-testid="quote-contact-phone">
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087]">
                <Phone size={14} className="text-[#0055FF]" /> {t.quote.labels.phone}
              </div>
              <a href={`tel:${t.phone1.replace(/\s/g, "")}`} className="block font-display text-xl text-white mt-2 hover:text-[#4D8BFF] transition-colors">{t.phone1}</a>
              <a href={`tel:${t.phone2.replace(/\s/g, "")}`} className="block font-display text-xl text-white mt-1 hover:text-[#4D8BFF] transition-colors">{t.phone2}</a>
            </div>
            <div data-testid="quote-contact-email">
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087]">
                <Mail size={14} className="text-[#0055FF]" /> {t.quote.labels.email}
              </div>
              <a href={`mailto:${t.email1}`} className="block font-mono text-sm text-white mt-2 hover:text-[#4D8BFF] transition-colors">{t.email1}</a>
              <a href={`mailto:${t.email2}`} className="block font-mono text-sm text-white mt-1 hover:text-[#4D8BFF] transition-colors">{t.email2}</a>
            </div>
            <div data-testid="quote-contact-address">
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#647087]">
                <MapPin size={14} className="text-[#0055FF]" /> {t.quote.labels.origin}
              </div>
              <p className="text-sm text-white mt-2">{t.address}</p>
            </div>
            <ul className="space-y-3 pt-4 border-t border-[#232733]">
              {t.quote.hints.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#A0AABF]">
                  <Check size={14} className="text-[#FF4D00] mt-1 shrink-0" /> {h}
                </li>
              ))}
            </ul>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#647087]">{t.quote.note}</p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="px-6 lg:px-14 py-16 lg:py-28">
          <form onSubmit={submit} className="space-y-7" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label className={labelCls} htmlFor="q-name">{f.name}</label>
                <input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-company">{f.company}</label>
                <input id="q-company" data-testid="quote-company-input" value={form.company} onChange={set("company")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-email">{f.email}</label>
                <input id="q-email" data-testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-phone">{f.phone}</label>
                <input id="q-phone" data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="q-product">{f.product}</label>
                <select id="q-product" data-testid="quote-product-select" value={form.product} onChange={set("product")} className={`${inputCls} appearance-none bg-[#0A0B0E]`}>
                  {f.products.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="q-quantity">{f.quantity}</label>
                <select id="q-quantity" data-testid="quote-quantity-select" value={form.quantity} onChange={set("quantity")} className={`${inputCls} appearance-none bg-[#0A0B0E]`}>
                  {f.quantities.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
            </div>
            <div>
              <span className={labelCls}>{f.customTitle}</span>
              <div className="grid sm:grid-cols-2 gap-4">
                {f.customOptions.map((opt, i) => (
                  <label key={opt} className="flex items-center gap-3 border border-[#232733] px-4 py-3 cursor-pointer hover:border-[#0055FF] transition-colors" data-testid={`quote-custom-${i}`}>
                    <Checkbox
                      checked={form.customization.includes(opt)}
                      onCheckedChange={toggleCustom(opt)}
                      className="border-[#232733] data-[state=checked]:bg-[#0055FF] data-[state=checked]:border-[#0055FF] rounded-none"
                    />
                    <span className="text-sm text-[#A0AABF]">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="q-details">{f.details}</label>
              <textarea id="q-details" data-testid="quote-details-textarea" rows={4} value={form.details} onChange={set("details")} className={`${inputCls} resize-none`} />
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-2">
              <button
                type="submit"
                data-testid="quote-submit-button"
                disabled={sending}
                className="group flex items-center gap-3 bg-[#FF4D00] text-white font-mono text-xs uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#0055FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? f.sending : f.submit}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {t.quote.reassure.map((r, i) => (
                  <span key={i} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#647087]">{r}</span>
                ))}
              </div>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Quote;
